import { ContraLogoTitleBlackOnWhiteIcon } from '#app/__generated__/components/Icons/ContraLogoTitleBlackOnWhite.js';
import { useCurrentUser } from '#app/hooks/useCurrentUser.js';
import {
  type IconLink as IconLinkType,
  type SectionColumn,
  sections,
  type TextLink as TextLinkType,
} from './constants.js';
import { css, styled } from 'styled-components';

const FooterContainer = styled.div<{ isDark?: boolean }>`
  ${({ isDark = false, theme }) => css`
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding-bottom: 72px;
    background-color: ${isDark ? theme.colorsV2.black : theme.colorsV2.white};
  `}
`;

const FooterInnerContainer = styled.div`
  ${({ theme }) => css`
    max-width: 100%;
    width: 100%;
    padding: 64px 16px 0;
    display: flex;
    flex-direction: column;
    gap: 64px;

    ${theme.mediaQueries.xl} {
      padding: 72px 4px 0;
      width: 1376px;
    }
  `}
`;

const TopSection = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: 16px;

    ${theme.mediaQueries.sm} {
      flex-direction: row;
      justify-content: space-between;
      flex-wrap: wrap;
    }
  `}
`;

const BottomSection = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 16px;

    ${theme.mediaQueries.lg} {
      width: 100%;
      flex-direction: row;
      justify-content: space-between;
      align-items: flex-end;
    }
  `}
`;

const LinkSectionContainer = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: 16px;

    ${theme.mediaQueries.sm} {
      gap: 40px;
    }
  `}
`;

const LinkSection = styled.div<{ isDark?: boolean }>`
  ${({ isDark = false, theme }) => css`
    color: ${isDark ? theme.colorsV2.white : theme.colorsV2.gray70};
    margin-top: 24px;
  `}
`;

const LinkSectionTitle = styled.div<{ isDark?: boolean }>`
  ${({ isDark, theme }) => css`
    color: ${isDark ? theme.colorsV2.white : theme.colorsV2.gray70};
    font-size: 10px;
    line-height: 16px;
    letter-spacing: 1.2px;
    text-transform: uppercase;
    padding-bottom: 16px;
  `}
`;

const TextLinkSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const SocialLinkSection = styled.div`
  display: flex;
  flex-direction: row;
  gap: 16px;
`;

const TextLink = styled.a`
  ${({ theme }) => css`
    font-weight: ${theme.fontWeights.bold};
    font-size: 16px;
    line-height: 24px;
    margin-bottom: 4px;
  `}
`;

const SocialLink = styled.a``;

const LegalLinksSection = styled.div`
  display: flex;
  flex-direction: row;
  gap: 16px;
`;

const Copyright = styled.div<{ isDark?: boolean }>`
  ${({ isDark, theme }) => css`
    color: ${isDark ? theme.colorsV2.white : theme.colorsV2.gray70};
    font-size: 12px;
    letter-spacing: 0.15px;
  `}
`;

const LegalLink = styled.a<{ isDark?: boolean }>`
  ${({ isDark, theme }) => css`
    color: ${isDark ? theme.colorsV2.white : theme.colorsV2.gray70};
    font-size: 12px;
    letter-spacing: 0.15px;

    &:not(:last-child) {
      padding-right: 16px;
      border-right: 1px solid
        ${isDark ? theme.colorsV2.white : theme.colorsV2.gray70};
    }
  `}
`;

const isTextLinkArray = (
  links: Array<IconLinkType | TextLinkType>,
): links is TextLinkType[] => {
  return links.every((link) => 'name' in link);
};

const TextOrIconLink = ({
  input,
}: {
  readonly input: IconLinkType | TextLinkType;
}) => {
  if ('icon' in input) {
    return (
      <SocialLink
        aria-label={`Contra's ${input.label} link`}
        href={input.link}
        rel="noreferrer noopener"
        target="_blank"
      >
        {input.icon}
      </SocialLink>
    );
  }

  return (
    <TextLink
      href={input.link}
      target="_blank"
    >
      {input.name}
    </TextLink>
  );
};

const Section = ({
  isDark = false,
  section,
}: {
  readonly isDark?: boolean;
  readonly section: SectionColumn;
}) => {
  if (Array.isArray(section)) {
    return (
      <LinkSectionContainer>
        {section.map((innerSection) => (
          <Section
            isDark={isDark}
            key={innerSection.title}
            section={innerSection}
          />
        ))}
      </LinkSectionContainer>
    );
  }

  const { links } = section;
  const areLinksTextual = isTextLinkArray(links);

  const Wrapper = areLinksTextual ? TextLinkSection : SocialLinkSection;

  return (
    <LinkSection isDark={isDark}>
      <LinkSectionTitle isDark={isDark}>{section.title}</LinkSectionTitle>

      <Wrapper>
        {section.links.map((link) => (
          <TextOrIconLink
            input={link}
            key={link.link}
          />
        ))}
      </Wrapper>
    </LinkSection>
  );
};

export const Footer = ({
  isDark = false,
  showWhenAuthenticated = false,
}: {
  readonly isDark?: boolean;
  readonly showWhenAuthenticated?: boolean;
}) => {
  const currentUser = useCurrentUser();

  if (currentUser && !showWhenAuthenticated) {
    // the footer should only be visible to non-logged in users
    return null;
  }

  return (
    <FooterContainer isDark={isDark}>
      <FooterInnerContainer>
        <TopSection>
          {sections.map((section, index) => (
            <Section
              isDark={isDark}
              key={`section-${index}`}
              section={section}
            />
          ))}
        </TopSection>

        <BottomSection>
          <a
            aria-label="Contra's homepage"
            href="/"
            target="_blank"
          >
            <ContraLogoTitleBlackOnWhiteIcon
              style={{
                height: '47px',
                width: '246px',
              }}
            />
          </a>

          <LegalLinksSection>
            <LegalLink
              href="https://contra.com/terms"
              isDark={isDark}
              target="_blank"
            >
              Terms & Conditions
            </LegalLink>
            <LegalLink
              href="https://contra.com/policies/privacy"
              isDark={isDark}
              target="_blank"
            >
              Privacy Policy
            </LegalLink>
            <LegalLink
              href="https://contra.com/policies/cookies"
              isDark={isDark}
              target="_blank"
            >
              Cookie Policy
            </LegalLink>
          </LegalLinksSection>

          <Copyright isDark={isDark}>
            © {new Date().getFullYear()} Contra.Work Inc All Rights Reserved.
          </Copyright>
        </BottomSection>
      </FooterInnerContainer>
    </FooterContainer>
  );
};
